import { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Table, TableHead, TableRow, TableCell,
  TableBody, Button, Alert, Chip, Divider, MenuItem, TextField, Dialog,
  DialogTitle, DialogContent, DialogActions, IconButton, CircularProgress, Tooltip,
  useMediaQuery, useTheme,
} from '@mui/material';
import { Add, Delete, Inventory2, Cancel } from '@mui/icons-material';
import axios from 'axios';
import { useLocations } from '../hooks/useLocations';

const API  = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const STATUS_COLOR = { Open: 'primary', Posted: 'success', Cancelled: 'default' };
const emptyLine = () => ({ _key: Date.now() + Math.random(), itemNo: '', description: '', qty: '' });
const today = () => new Date().toISOString().split('T')[0];

export default function StaffRequisitions() {
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { locations } = useLocations();

  const [reqs,    setReqs]    = useState([]);
  const [items,   setItems]   = useState([]);
  const [stockMap, setStockMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');
  const [success, setSuccess] = useState('');

  const [dlgOpen, setDlgOpen] = useState(false);
  const [header,  setHeader]  = useState({ requisitionDate: today(), fromLocationCode: '', department: '', description: '' });
  const [lines,   setLines]   = useState([emptyLine()]);
  const [saving,  setSaving]  = useState(false);
  const [formErr, setFormErr] = useState('');

  const flash = (m) => { setSuccess(m); setTimeout(() => setSuccess(''), 3000); };

  const loadReqs = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/payables/requisitions/my`, { headers: hdrs() });
      setReqs(data || []);
    } catch (err) { setError(err.response?.data?.error || 'Failed to load your requisitions'); }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const [it, st] = await Promise.all([
          axios.get(`${API}/api/payables/items`,      { headers: hdrs() }),
          axios.get(`${API}/api/payables/item-stock`, { headers: hdrs() }).catch(() => ({ data: {} })),
        ]);
        setItems((it.data || []).filter(i => i.itemType === 'Item'));
        setStockMap(st.data || {});
      } catch { /* items optional */ }
      await loadReqs();
      setLoading(false);
    })();
  }, [loadReqs]);

  const stockAt = (itemNo) => {
    const s = stockMap[itemNo];
    if (!s || !header.fromLocationCode) return null;
    return s.byLocation?.[header.fromLocationCode]?.qty ?? 0;
  };

  const updateLine = (key, field, value) => {
    setLines(prev => prev.map(l => {
      if (l._key !== key) return l;
      const u = { ...l, [field]: value };
      if (field === 'itemNo') u.description = items.find(i => i.itemNo === value)?.itemName || '';
      return u;
    }));
  };
  const addLine    = () => setLines(prev => [...prev, emptyLine()]);
  const removeLine = (key) => setLines(prev => prev.filter(l => l._key !== key));

  const openDlg = () => {
    setHeader({ requisitionDate: today(), fromLocationCode: '', department: '', description: '' });
    setLines([emptyLine()]); setFormErr(''); setDlgOpen(true);
  };

  const save = async () => {
    setFormErr('');
    if (!header.fromLocationCode) { setFormErr('Issue From Location is required'); return; }
    for (let i = 0; i < lines.length; i++) {
      if (!lines[i].itemNo) { setFormErr(`Line ${i + 1}: Item is required`); return; }
      if (!lines[i].qty || parseFloat(lines[i].qty) <= 0) { setFormErr(`Line ${i + 1}: Quantity must be > 0`); return; }
    }
    setSaving(true);
    try {
      await axios.post(`${API}/api/payables/requisitions/my`, {
        ...header,
        lines: lines.map(l => ({ itemNo: l.itemNo, description: l.description, qty: parseFloat(l.qty) })),
      }, { headers: hdrs() });
      setDlgOpen(false);
      flash('Requisition raised (status: Open). The storekeeper will review and post it.');
      loadReqs();
    } catch (err) {
      setFormErr(err.response?.data?.error || 'Failed to save requisition');
    } finally { setSaving(false); }
  };

  const cancel = async (reqNo) => {
    try {
      await axios.delete(`${API}/api/payables/requisitions/my/${reqNo}`, { headers: hdrs() });
      flash('Requisition cancelled');
      loadReqs();
    } catch (err) { setError(err.response?.data?.error || 'Failed to cancel'); }
  };

  if (loading) return (
    <Box sx={{ textAlign: 'center', mt: 8 }}><CircularProgress /><Typography mt={2}>Loading…</Typography></Box>
  );

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', mt: { xs: 1.5, sm: 4 }, mb: 4, px: { xs: 1.5, sm: 2 } }}>
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
      {error   && <Alert severity="error"   sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Card elevation={3}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
            <Box display="flex" alignItems="center" gap={1}>
              <Inventory2 sx={{ color: '#2e7d32' }} />
              <Typography variant="h6" fontWeight={700}>My Store Requisitions</Typography>
            </Box>
            <Button variant="contained" startIcon={<Add />} onClick={openDlg}>New Requisition</Button>
          </Box>
          {reqs.length === 0 ? (
            <Typography variant="body2" color="text.secondary" py={3} textAlign="center">No requisitions yet.</Typography>
          ) : isMobile ? (
            /* ── Phone: card list ── */
            <Box display="flex" flexDirection="column" gap={1.2}>
              {reqs.map(r => (
                <Card key={r.requisitionNo} variant="outlined" sx={{ borderRadius: 2 }}>
                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Box display="flex" alignItems="center" gap={1} mb={0.6}>
                      <Typography variant="body2" fontWeight={700} fontFamily="monospace" noWrap>{r.requisitionNo}</Typography>
                      <Chip label={r.status} size="small" color={STATUS_COLOR[r.status] || 'default'} variant="outlined" sx={{ ml: 'auto' }} />
                    </Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {r.requisitionDate ? new Date(r.requisitionDate).toLocaleDateString() : '—'}
                      {r.fromLocationCode ? ` · ${r.fromLocationCode}` : ''}
                      {` · ${r.lines?.length || 0} line${(r.lines?.length || 0) !== 1 ? 's' : ''}`}
                    </Typography>
                    {r.status === 'Open' && (
                      <Button size="small" variant="outlined" color="error" startIcon={<Cancel />}
                        onClick={() => cancel(r.requisitionNo)} sx={{ mt: 1 }} fullWidth>Cancel</Button>
                    )}
                  </CardContent>
                </Card>
              ))}
            </Box>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 640 }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell><strong>Req No</strong></TableCell>
                    <TableCell><strong>Date</strong></TableCell>
                    <TableCell><strong>Location</strong></TableCell>
                    <TableCell align="center"><strong>Lines</strong></TableCell>
                    <TableCell align="center"><strong>Status</strong></TableCell>
                    <TableCell align="right"><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reqs.map(r => (
                    <TableRow key={r.requisitionNo} hover>
                      <TableCell sx={{ fontFamily: 'monospace' }}><strong>{r.requisitionNo}</strong></TableCell>
                      <TableCell>{r.requisitionDate ? new Date(r.requisitionDate).toLocaleDateString() : '—'}</TableCell>
                      <TableCell>{r.fromLocationCode || '—'}</TableCell>
                      <TableCell align="center">{r.lines?.length || 0}</TableCell>
                      <TableCell align="center"><Chip label={r.status} size="small" color={STATUS_COLOR[r.status] || 'default'} variant="outlined" /></TableCell>
                      <TableCell align="right">
                        {r.status === 'Open' && (
                          <Tooltip title="Cancel">
                            <IconButton size="small" color="error" onClick={() => cancel(r.requisitionNo)}><Cancel fontSize="small" /></IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* New requisition dialog */}
      <Dialog open={dlgOpen} onClose={() => setDlgOpen(false)} maxWidth="md" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ pb: 1 }}>
          <Box display="flex" alignItems="center" gap={1}>
            <Inventory2 fontSize="small" color="primary" />
            <Typography fontWeight={700}>New Store Requisition</Typography>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          <Box display="flex" flexWrap="wrap" gap={2} mb={2}>
            <TextField label="Date" type="date" size="small" value={header.requisitionDate}
              onChange={e => setHeader({ ...header, requisitionDate: e.target.value })}
              InputLabelProps={{ shrink: true }} sx={{ width: { xs: '100%', sm: 170 } }} />
            <TextField select label="Issue From Location" size="small" value={header.fromLocationCode}
              onChange={e => setHeader({ ...header, fromLocationCode: e.target.value })}
              sx={{ width: { xs: '100%', sm: 240 } }}>
              <MenuItem value=""><em>Select Location</em></MenuItem>
              {locations.map(loc => (
                <MenuItem key={loc.locationCode} value={loc.locationCode}>{loc.locationCode} – {loc.locationName}</MenuItem>
              ))}
            </TextField>
            <TextField label="Department" size="small" value={header.department}
              onChange={e => setHeader({ ...header, department: e.target.value })} sx={{ width: { xs: '100%', sm: 160 } }} />
            <TextField label="Purpose / Description" size="small" value={header.description}
              onChange={e => setHeader({ ...header, description: e.target.value })} sx={{ flex: 1, minWidth: 200 }} />
          </Box>

          {isMobile ? (
            /* ── Phone: stacked line cards ── */
            <Box display="flex" flexDirection="column" gap={1.2}>
              {lines.map((line, idx) => {
                const avail = stockAt(line.itemNo);
                const over = avail !== null && parseFloat(line.qty) > avail;
                return (
                  <Card key={line._key} variant="outlined" sx={{ borderRadius: 2 }}>
                    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                        <Typography variant="caption" color="text.secondary" fontWeight={700}>Item {idx + 1}</Typography>
                        {lines.length > 1 && (
                          <IconButton size="small" color="error" onClick={() => removeLine(line._key)}><Delete fontSize="small" /></IconButton>
                        )}
                      </Box>
                      <TextField select size="small" fullWidth label="Item" value={line.itemNo} sx={{ mb: 1.2 }}
                        onChange={e => updateLine(line._key, 'itemNo', e.target.value)}>
                        <MenuItem value=""><em>Select Item</em></MenuItem>
                        {items.map(i => <MenuItem key={i.itemNo} value={i.itemNo}>{i.itemNo} – {i.itemName}</MenuItem>)}
                      </TextField>
                      <Box display="flex" gap={1.5} alignItems="flex-start">
                        <TextField size="small" type="number" label="Qty" value={line.qty} error={over}
                          helperText={over ? 'Exceeds stock' : ''} inputProps={{ min: 0.001, step: '0.001' }}
                          onChange={e => updateLine(line._key, 'qty', e.target.value)} sx={{ flex: 1 }} />
                        <Box sx={{ flex: 1, pt: 0.5 }}>
                          <Typography variant="caption" color="text.secondary" display="block">In stock</Typography>
                          {line.itemNo && avail !== null
                            ? <Chip label={avail.toFixed(2)} size="small" variant="outlined" color={avail > 0 ? 'success' : 'error'} sx={{ fontWeight: 600, mt: 0.3 }} />
                            : <Typography variant="caption" color="text.disabled">{line.itemNo ? (header.fromLocationCode ? '0.00' : 'pick location') : '—'}</Typography>}
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                );
              })}
            </Box>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 560 }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ width: '40%' }}><strong>Item</strong></TableCell>
                    <TableCell align="right"><strong>In Stock</strong></TableCell>
                    <TableCell sx={{ width: 130 }}><strong>Qty</strong></TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lines.map(line => {
                    const avail = stockAt(line.itemNo);
                    const over = avail !== null && parseFloat(line.qty) > avail;
                    return (
                      <TableRow key={line._key}>
                        <TableCell>
                          <TextField select size="small" fullWidth value={line.itemNo}
                            onChange={e => updateLine(line._key, 'itemNo', e.target.value)}>
                            <MenuItem value=""><em>Select Item</em></MenuItem>
                            {items.map(i => <MenuItem key={i.itemNo} value={i.itemNo}>{i.itemNo} – {i.itemName}</MenuItem>)}
                          </TextField>
                        </TableCell>
                        <TableCell align="right">
                          {line.itemNo && avail !== null
                            ? <Chip label={avail.toFixed(2)} size="small" variant="outlined" color={avail > 0 ? 'success' : 'error'} sx={{ fontWeight: 600 }} />
                            : (line.itemNo ? <Typography variant="caption" color="text.disabled">{header.fromLocationCode ? '0.00' : 'pick location'}</Typography> : '')}
                        </TableCell>
                        <TableCell>
                          <TextField size="small" type="number" fullWidth value={line.qty} error={over}
                            helperText={over ? 'Exceeds stock' : ''} inputProps={{ min: 0.001, step: '0.001' }}
                            onChange={e => updateLine(line._key, 'qty', e.target.value)} />
                        </TableCell>
                        <TableCell>
                          {lines.length > 1 && (
                            <IconButton size="small" color="error" onClick={() => removeLine(line._key)}><Delete fontSize="small" /></IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          )}
          <Button startIcon={<Add />} onClick={addLine} variant="outlined" size="small" sx={{ mt: 1 }} fullWidth={isMobile}>Add Line</Button>

          {formErr && <Alert severity="error" sx={{ mt: 2 }}>{formErr}</Alert>}
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 2, py: 1.5 }}>
          <Button onClick={() => setDlgOpen(false)} size="small">Cancel</Button>
          <Button variant="contained" size="small" onClick={save} disabled={saving}
            startIcon={saving ? <CircularProgress size={14} /> : <Add fontSize="small" />}>
            {saving ? 'Saving…' : 'Raise Requisition'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
